export function enrichDetails(product) {
  const common = {
    materials: 'Stainless steel, medical-grade alloys',
    sterilization: 'Autoclave compatible; follow hospital sterilization protocols',
  };
  const byCategory = {
    cutting: {
      specs: 'Precision edges for clean incisions; various blade/length options',
      usage: 'Incision and tissue cutting in surgical procedures',
    },
    grasping: {
      specs: 'Serrated/atraumatic jaws; varying tip styles for tissue handling',
      usage: 'Securely hold or manipulate tissues and surgical materials',
    },
    clamping: {
      specs: 'Locking mechanisms with curved/straight jaws for hemostasis',
      usage: 'Occlude blood vessels to control bleeding during surgery',
    },
    suturing: {
      specs: 'Ergonomic needle holders with secure jaw grip and fine control',
      usage: 'Drive needles and place sutures for wound closure',
    },
    retracting: {
      specs: 'Hand-held/self-retaining frames for tissue retraction',
      usage: 'Expose surgical field by displacing tissues safely',
    },
    orthopedic: {
      specs: 'Robust instruments for bone manipulation and fixation',
      usage: 'Orthopedic procedures involving bone cutting, drilling, and plating',
    },
    microsurgery: {
      specs: 'Ultra-fine tips with high precision and minimal trauma',
      usage: 'Delicate procedures using magnification',
    },
    laparoscopic: {
      specs: 'Long-shafted, insulated tools for minimally invasive access',
      usage: 'Operate through small ports with camera guidance',
    },
    neurosurgery: {
      specs: 'Fine, atraumatic designs suitable for neural tissues',
      usage: 'Procedures involving brain/spinal structures',
    },
    electrosurgery: {
      specs: 'Insulated tips and connectors; compatible with ESU generators',
      usage: 'Cutting/coagulation using electrical current',
    },
    suction: {
      specs: 'Sterile suction tips with varying diameters',
      usage: 'Remove fluids and debris from surgical field',
    },
    thoracic: {
      specs: 'Specialized instruments for thoracic cavity procedures',
      usage: 'Exposure and manipulation within chest cavity',
    },
    gynecology: {
      specs: 'Designed for gynecological anatomy and procedures',
      usage: 'Diagnostic and operative gynecologic surgery',
    },
    cardiology: {
      specs: 'Instruments suitable for cardiac tissue and vessels',
      usage: 'Cardiothoracic surgery and interventions',
    },
    urology: {
      specs: 'Endoscopic and open-surgery tools for urinary tract',
      usage: 'Urologic diagnostics and operations',
    },
    // Added category-specific details for the new catalog keys
    general: {
      specs: 'Common surgical forceps for tissue handling, dissection, and hemostasis; straight/curved options',
      usage: 'General surgical procedures requiring grasping, clamping, or delicate handling',
    },
    dental: {
      specs: 'Anatomically contoured jaws for secure tooth engagement (molar, premolar, incisor, canine, root)',
      usage: 'Dental extractions and oral surgery procedures',
    },
    ophthalmic: {
      specs: 'Micro-forceps with fine tips and precise control suitable for delicate ocular tissues',
      usage: 'Ophthalmic microsurgery including capsulorhexis, corneal work, lens handling',
    },
    gynecology_obstetric: {
      specs: 'Forceps tailored for gynecologic and obstetric use (uterine dressing, sponge holding, vulsellum, ovum)',
      usage: 'Gynecology and obstetrics procedures including tissue handling and instrument guidance',
    },
    specialty: {
      specs: 'Specialized ENT/orthopedic instruments such as bone holding/cutting, nasal, biopsy, and alligator forceps',
      usage: 'Specialty procedures in ENT, orthopedics, and targeted surgical applications',
    },
  };

  const categoryDetails = byCategory[product.category] || { specs: 'General-purpose surgical instrument', usage: 'Clinical/surgical use' };
  return {
    ...common,
    ...categoryDetails,
    source: 'Details to be enriched from verified medical sources',
  };
}

// Realistic instrument names to replace generic labels
export const instrumentNames = [
  'Scalpel #10',
  'Scalpel #11',
  'Metzenbaum Scissors',
  'Mayo Scissors (Straight)',
  'Mayo Scissors (Curved)',
  'Iris Scissors',
  'Adson Forceps (Toothed)',
  'Adson Forceps (Atraumatic)',
  'Tissue Forceps (Toothed)',
  'DeBakey Tissue Forceps',
  'Kelly Hemostat (Straight)',
  'Kelly Hemostat (Curved)',
  'Mosquito Hemostat (Halsted)',
  'Crile Hemostat',
  'Kocher Forceps',
  'Allis Tissue Forceps',
  'Babcock Forceps',
  'Backhaus Towel Clamp',
  'Mayo-Hegar Needle Holder',
  'Olsen-Hegar Needle Holder',
  'Crile-Wood Needle Holder',
  'Senn Retractor',
  'Army-Navy Retractor',
  'Gelpi Retractor',
  'Weitlaner Retractor',
  'Richardson Retractor',
  'Langenbeck Retractor',
  'Rake Retractor',
  'Hohmann Retractor',
  'Freer Periosteal Elevator',
  'Cobb Elevator',
  'Bone Curette',
  'Leksell Rongeur',
  'Kerrison Rongeur',
  'Bone Mallet',
  'Osteotome',
  'Bone Saw',
  'Gigli Saw',
  'Frazier Suction Tip',
  'Yankauer Suction Tip',
  'Poole Suction',
  'Hegar Dilators',
  'Tenaculum',
  'Graves Vaginal Speculum',
  'Cusco Vaginal Speculum',
  'Veress Needle',
  'Laparoscopic Trocar',
  'Maryland Dissector (Lap)',
  'Laparoscopic Grasper',
  'Laparoscopic Scissors',
  'Electrocautery Pencil',
  'Bipolar Forceps',
  'Monopolar Hook',
  'Clip Applier',
  'Endoscopic Stapler',
  'Hemoclip Applier',
  'Bulldog Vascular Clamp',
  'Satinsky Vascular Clamp',
  'DeBakey Vascular Clamp',
  'Potts Scissors',
  'Castroviejo Needle Holder',
  'Micro Scissors',
  'Bayonet Forceps',
  'Penfield Dissector',
  'Dura Hook',
  'Neurosurgical Suction',
  'Kerrison Punch',
  'Kerrison Rongeur (2mm)',
  'Kerrison Rongeur (3mm)',
  'Kerrison Rongeur (5mm)',
];

// New catalog based on user-provided categories and images in public/images
export const productCatalog = {
  general: [
    { name: 'Adson Forceps', image: '/images/adson forceps.jpg' },
    { name: 'DeBakey Forceps', image: '/images/Debajey forceps.jpg' },
    { name: 'Kelly Forceps (Straight)', image: '/images/Kelly Forceps Straight End.jpg' },
    { name: 'Kelly Forceps (Curved)', image: '/images/Kelly Forceps curved end.jpg' },
    { name: 'Mosquito Forceps (Straight)', image: '/images/Mosquito Forceps straigh end.jpg' },
    { name: 'Mosquito Forceps (Curved)', image: '/images/Curved Mosquito Forceps.jpg' },
    { name: 'Crile Forceps', image: '/images/Multigate Crile Forceps.jpg' },
    { name: 'Rochester-Pean Hemostat', image: '/images/Rochester Pean Hemostat Forceps Straight & Curved Clamps.jpg' },
    { name: 'Kocher Artery Forceps', image: '/images/Kocher Artery Forcep.jpg' },
    { name: 'Allis Tissue Forceps', image: '/images/Allis Tissue Forceps.jpg' },
    { name: 'Babcock Forceps', image: '/images/babcock forceps.jpg' },
    { name: 'Spencer Wells Forceps', image: '/images/Spencer Wells Forceps.JPG' },
    { name: 'Russian Tissue Forceps', image: '/images/Russian Tissue Forceps.jpg' },
    { name: 'Magill Forceps', image: '/images/Magill Forceps.jpg' },
    { name: 'Dressing Forceps', image: '/images/Dressing Tunneling Forceps.jpg' },
    { name: 'Thumb Forceps (Adson)', image: '/images/adson forceps.jpg' },
    { name: 'Foerster Sponge Forceps', image: '/images/Foerster Forceps.jpg' },
    { name: 'Grasping Forceps', image: '/images/Grasping Forceps.jpg' },
    { name: 'Cushing Forceps', image: '/images/Cushing Forceps.jpg' },
    { name: 'Towel Clamp (Backhaus)', image: '/images/towel clamp.JPG' },
    { name: 'Needle Holder', image: '/images/Needle holder.jpg' },
    { name: 'Bandage Scissors', image: '/images/bandage scissors.jpg' },
    { name: 'Metzenbaum Scissors', image: '/images/metzenbaum scissors.jpg' },
    { name: 'Mayo Scissors', image: '/images/mayo scissor.jpg' },
    { name: 'Dressing Scissors', image: '/images/dressing scissor.jpg' },
    { name: 'Holloware', image: '/images/holloware.jpg' },
    { name: 'Cheatle Forcep', image: '/images/cheatle forcep.jpg' },
    { name: 'Needle Holder TC', image: '/images/Needle holder tc.jpg' },
    { name: 'Olsen Hegar Needle Holder', image: '/images/olsen heger needle holder.jpg' },
    { name: 'Teale Vulsellum Forcep', image: '/images/teale vulsellum forcep.jpg' },
    { name: 'Scalpel Handle', image: '/images/scalpel handle.jpg' },
    { name: 'Suture Scissor', image: '/images/suture scissor.jpg' },
  ],
  dental: [
    { name: 'Upper Molar Extracting Forceps', image: '/images/Upper Molar Extracting Forceps.jpg' },
    { name: 'Lower Molar Extracting Forceps', image: '/images/Lower Molar Extracting Forceps.jpg' },
    { name: 'Cryer Extracting Forceps (Bicuspids/Incisors/Roots)', image: '/images/Dental Extracting Forceps Cryer Bicuspids , Incisors and Roots.jpg' },
    { name: 'Root Extracting Forceps', image: '/images/Root Extracting Forceps.jpg' },
    { name: 'Cowhorn Forceps', image: '/images/Cowhorn Forceps.jpg' },
    { name: 'Bayonet Forceps', image: '/images/Bayonet Forceps.jpg' },
    { name: 'Dental Elevator', image: '/images/dental elevator.jpg' },
    { name: 'Mouth Gag', image: '/images/mouth gag.jpg' },
    { name: 'Dental Mirror', image: '/images/dental mirror.jpg' },
    { name: 'Dental Probe', image: '/images/dental probe.jpg' },
    { name: 'Impression Tray', image: '/images/impression tray.jpg' },
    { name: 'Dental Plier', image: '/images/dental plier.jpg' },
    { name: 'Burnisher', image: '/images/burnisher.jpg' },
    { name: 'Plugger', image: '/images/plugger.jpg' },
    { name: 'Amalgam Carrier', image: '/images/amalgam carrier.jpg' },
    { name: 'Cement Spatula', image: '/images/cement spatula.jpg' },
    { name: 'Wax Carver', image: '/images/wax carver.jpg' },
    ],
    ophthalmic: [
    { name: 'Iris Forceps', image: '/images/Iris Forceps.jpg' },
    { name: 'Corneal Forceps', image: '/images/Corneal Forceps.jpg' },
    { name: 'Capsulorhexis Forceps', image: '/images/Capsulorhexis Forceps.jpg' },
    { name: 'Fixation Forceps', image: '/images/Fixation Forceps.jpg' },
    { name: 'Bishop-Harmon Forceps', image: '/images/Bishop-Harmon Forceps.jpg' },
    { name: 'McPherson Forceps', image: '/images/McPherson Forceps.jpg' },
    { name: 'Bonn Forceps', image: '/images/Bonn Forceps.jpg' },
  ],
  gynecology_obstetric: [
    { name: 'Uterine Dressing Forceps', image: '/images/Uterine Dressing Forceps.jpg' },
    { name: 'Sponge Holding Forceps', image: '/images/Sponge Holding Forceps.jpg' },
    { name: 'Foerster Forceps', image: '/images/Foerster Forceps.jpg' },
    { name: 'Vulsellum Forceps', image: '/images/Vulsellum Forceps.jpg' },
    { name: 'Ovum Forceps', image: '/images/Ovum Forceps.jpg' },
    { name: 'Kocher’s Obstetric Forceps', image: '/images/Kocher’s Obstetric Forceps.jpg' },
    { name: 'Littlewood Tissue & Intestinal Holding Forceps', image: '/images/Littlewood Tissue and Intestinal Holding Forceps Surgical Forceps Instruments.jpg' },
    { name: 'Lateral Vaginal Retractor', image: '/images/lateral veginal retractor.jpg' },
  ],
  specialty: [
    { name: 'Bone Holding Forceps', image: '/images/Bone Holding Forceps.jpg' },
    { name: 'Lane Bone Holding Forcep', image: '/images/lane bone holding forcep.jpg' },
    { name: 'Bone Cutting Forceps', image: '/images/Bone Cutting Forceps.JPG' },
    { name: 'Ruskin Bone Splitting Forceps', image: '/images/Rusking bone splitting forceps.jpg' },
    { name: 'Tilley Nasal Forceps', image: '/images/Tilley Nasal Forceps.jpg' },
    { name: 'Hartmann Alligator Forceps', image: '/images/Hartmann Alligator Forceps.jpg' },
    { name: 'Ear Dressing Forceps', image: '/images/Ear Dressing Forceps.jpg' },
    { name: 'Biopsy Forceps', image: '/images/Biopsy Forceps.jpg' },
    { name: 'Cushing Forceps', image: '/images/Cushing Forceps.jpg' },
    { name: 'Hartman Forceps', image: '/images/hartman forceps.jpg' },
    { name: 'Bone File', image: '/images/bone file.jpg' },
    { name: 'Bone Chisel', image: '/images/bone chisel.jpg' },
    { name: 'Bone Mallet', image: '/images/bone mallet.jpg' },
    { name: 'Periosteal Elevator', image: '/images/periosteal elevator.jpg' },
    { name: 'Bone Curette', image: '/images/curette.jpg' },
    { name: 'Verbrugge Forcep', image: '/images/verbrugge forcep.jpg' },
    { name: 'Bipolar Forcep', image: '/images/bipolar forcep.jpg' },
    { name: 'Wire Twisting Forcep', image: '/images/wire twisting forcep.jpg' },
    { name: 'Pin Cutter TC Inserted', image: '/images/pin cutter tc inserted.jpg' },
    { name: 'Electro Surgical Pencil', image: '/images/electro surgical pencil.jpg' },
  ],
};